/**
 * Create LegacyQuestionniare from ReviewQuestionnaire.
 */

import {
    LegacyCheckboxesQuestionPayload,
    LegacyDropdownQuestionPayload,
    LegacyGraphDefinition,
    LegacyHeadingSection,
    LegacyOverallRating,
    LegacyOverallRatingsDefinition,
    LegacyRating,
    LegacyReviewQuestionnaire,
    LegacyScalesQuestionPayload,
    LegacySecondaryOverallRatingsDefinition,
    LegacySection,
    LegacySliderQuestionPayload,
    LegacyTextQuestionPayload,
    Meta,
} from '../../../../../types/LegacyReviewQuestionnaire';
import {
    AssessmentCheckboxesQuestion,
    AssessmentDropdownQuestion,
    AssessmentRating,
    AssessmentScalesQuestion,
    AssessmentSliderQuestion,
    AssessmentTextQuestion,
    AssessmentTextWithRatingQuestion,
    Graph,
    Heading,
    OverallRating,
    Question,
    ReviewQuestionnaire,
    SecondaryOverallRating,
    Section,
} from '../../../../../types/ReviewQuestionnaire';

const fromGraph = (graph: Graph): LegacyGraphDefinition => ({
    enabled: true,
    requiredForManager: graph.reviewerAnswerRequired,
    requiredForReviewee: graph.revieweeAnswerRequired,
    confidentialGraph: graph.isConfidential,
    xAxis: {
        description: graph.graphPayload.xDescription,
        high: graph.graphPayload.xHigh,
        label: graph.graphPayload.xLabel,
        low: graph.graphPayload.xLow,
    },
    yAxis: {
        description: graph.graphPayload.yDescription,
        high: graph.graphPayload.yHigh,
        label: graph.graphPayload.yLabel,
        low: graph.graphPayload.yLow,
    },
});

const fromHeading = (heading: Heading, index: number): LegacyHeadingSection => ({
    id: `h${index}`,
    title: heading.headingPayload.title,
    description: heading.headingPayload.description,
    questionType: 'HEADING',
    type: 'ReviewHeading',
    manager: {},
    reviewee: {},
});

const fromAssessmentRatingToOverallRating = (
    assessmentRating: AssessmentRating,
    index: number
): LegacyOverallRating => ({
    code: assessmentRating.code,
    description: assessmentRating.description || '',
    label: assessmentRating.name,
    value: index, // @todo Right?
});

const fromOverallRating = (overallRating: OverallRating): LegacyOverallRatingsDefinition => ({
    confidential: overallRating.overallRatingPayload.confidential,
    enabled: true,
    enabledForManager: overallRating.enabledForManager,
    enabledForReviewee: overallRating.enabledForReviewee,
    ratings: overallRating.overallRatingPayload.ratings?.map(fromAssessmentRatingToOverallRating) ?? [],
    requiredForReviewee: overallRating.revieweeAnswerRequired,
    requiredForManager: overallRating.reviewerAnswerRequired,
});

const fromSecondaryOverallRating = (
    overallRating: SecondaryOverallRating
): LegacySecondaryOverallRatingsDefinition => ({
    enabled: true,
    enabledForManager: overallRating.enabledForManager,
    enabledForReviewee: overallRating.enabledForReviewee,
    ratings: overallRating.overallRatingPayload.ratings?.map(fromAssessmentRatingToOverallRating) ?? [],
    requiredForReviewee: overallRating.revieweeAnswerRequired,
    requiredForManager: overallRating.reviewerAnswerRequired,
});

const fromAssessmentRating = (assessmentRating: AssessmentRating): LegacyRating => ({
    id: assessmentRating.code,
    text: assessmentRating.name,
});

const fromAssessmentCheckboxesQuestion = (
    assessmentQuestion: AssessmentCheckboxesQuestion
): LegacyCheckboxesQuestionPayload => ({
    allowComment: assessmentQuestion.allowComment,
    question: assessmentQuestion.description,
    scale: assessmentQuestion.ratings.map(fromAssessmentRating),
});

const fromAssessmentDropdownQuestion = (
    assessmentQuestion: AssessmentDropdownQuestion
): LegacyDropdownQuestionPayload => ({
    allowComment: assessmentQuestion.allowComment,
    question: assessmentQuestion.description,
    ratings: assessmentQuestion.ratings.map(fromAssessmentRating),
});

const fromAssessmentScalesQuestion = (assessmentQuestion: AssessmentScalesQuestion): LegacyScalesQuestionPayload => ({
    allowComment: assessmentQuestion.allowComment,
    question: assessmentQuestion.description,
    scale: assessmentQuestion.ratings.map(fromAssessmentRating),
});

const fromAssessmentSliderQuestion = (assessmentQuestion: AssessmentSliderQuestion): LegacySliderQuestionPayload => ({
    allowComment: assessmentQuestion.allowComment,
    question: assessmentQuestion.description,
});

const fromAssessmentTextQuestion = (assessmentQuestion: AssessmentTextQuestion): LegacyTextQuestionPayload => ({
    question: assessmentQuestion.description,
    ratings: [],
    ratingsEnabled: false,
});

const fromAssessmentTextWithRatingQuestion = (
    assessmentQuestion: AssessmentTextWithRatingQuestion
): LegacyTextQuestionPayload => ({
    question: assessmentQuestion.description,
    ratings: assessmentQuestion.ratings.map(fromAssessmentRating),
    ratingsEnabled: true,
});

const fromQuestion = (question: Question, index: number): LegacySection => {
    const revieweeQuestionType = question.revieweePayload?._tag;
    const reviewerQuestionType = question.reviewerPayload?._tag;
    const payloadType = (revieweeQuestionType || reviewerQuestionType)!;
    switch (payloadType) {
        case 'CHECKBOXES':
            return {
                id: `q${index}`,
                manager: fromAssessmentCheckboxesQuestion(question.reviewerPayload as AssessmentCheckboxesQuestion),
                reviewee: fromAssessmentCheckboxesQuestion(question.revieweePayload as AssessmentCheckboxesQuestion),
                confidentialTopic: question.isConfidential,
                requiredForManager: question.reviewerPayload!.isRequired,
                requiredForReviewee: question.revieweePayload!.isRequired,
                questionType: 'CHECKBOXES',
                type: 'ReviewCheckboxes',
            };
        case 'DROPDOWN':
            return {
                id: `q${index}`,
                manager: fromAssessmentDropdownQuestion(question.reviewerPayload as AssessmentDropdownQuestion),
                reviewee: fromAssessmentDropdownQuestion(question.revieweePayload as AssessmentDropdownQuestion),
                confidentialTopic: question.isConfidential,
                requiredForManager: question.reviewerPayload!.isRequired,
                requiredForReviewee: question.revieweePayload!.isRequired,
                questionType: 'DROPDOWN',
                type: 'ReviewDropdown',
            };
        case 'SCALES':
            return {
                id: `q${index}`,
                manager: fromAssessmentScalesQuestion(question.reviewerPayload as AssessmentScalesQuestion),
                reviewee: fromAssessmentScalesQuestion(question.revieweePayload as AssessmentScalesQuestion),
                confidentialTopic: question.isConfidential,
                requiredForManager: question.reviewerPayload!.isRequired,
                requiredForReviewee: question.revieweePayload!.isRequired,
                questionType: 'SCALES',
                type: 'ReviewScale',
            };
        case 'SLIDER':
            return {
                id: `q${index}`,
                manager: fromAssessmentSliderQuestion(question.reviewerPayload as AssessmentSliderQuestion),
                reviewee: fromAssessmentSliderQuestion(question.revieweePayload as AssessmentSliderQuestion),
                confidentialTopic: question.isConfidential,
                requiredForManager: question.reviewerPayload!.isRequired,
                requiredForReviewee: question.revieweePayload!.isRequired,
                sliderLabels: (question.reviewerPayload as AssessmentSliderQuestion).sliderLabels,
                sliderSteps: (question.reviewerPayload as AssessmentSliderQuestion).sliderSteps,
                questionType: 'SLIDER',
                type: 'ReviewSlider',
            };
        case 'TEXT':
        case 'TEXT_WITH_RATING':
            return {
                id: `q${index}`,
                manager:
                    reviewerQuestionType === 'TEXT_WITH_RATING'
                        ? fromAssessmentTextWithRatingQuestion(
                              question.reviewerPayload as AssessmentTextWithRatingQuestion
                          )
                        : fromAssessmentTextQuestion(question.reviewerPayload as AssessmentTextQuestion),
                reviewee:
                    revieweeQuestionType === 'TEXT_WITH_RATING'
                        ? fromAssessmentTextWithRatingQuestion(
                              question.revieweePayload as AssessmentTextWithRatingQuestion
                          )
                        : fromAssessmentTextQuestion(question.revieweePayload as AssessmentTextQuestion),
                confidentialTopic: question.isConfidential,
                requiredForManager: question.reviewerPayload!.isRequired,
                requiredForReviewee: question.revieweePayload!.isRequired,
                questionType: 'TEXT',
                type: 'ReviewQuestion',
            };
    }
};

const fromSection = (section: Section, index: number) => {
    switch (section._tag) {
        case 'graph':
            return { type: 'graph' as 'graph', res: fromGraph(section) };
        case 'heading':
            return { type: 'heading' as 'heading', res: fromHeading(section, index) };
        case 'overallRating':
            return { type: 'overallRating' as 'overallRating', res: fromOverallRating(section) };
        case 'secondaryOverallRating':
            return {
                type: 'secondaryOverallRating' as 'secondaryOverallRating',
                res: fromSecondaryOverallRating(section),
            };
        case 'question':
            return { type: 'question' as 'question', res: fromQuestion(section, index) };
    }
};

export const fromReviewQuestionnaire =
    (defaultQuestionnaire: LegacyReviewQuestionnaire) =>
    (reviewQuestionnaire: ReviewQuestionnaire): [Meta, LegacyReviewQuestionnaire] => {
        const sections = reviewQuestionnaire.sections.map(fromSection);

        const graph = (sections.find(x => x.type === 'graph')?.res ||
            defaultQuestionnaire.graph) as LegacyGraphDefinition;
        const overallRating = (sections.find(x => x.type === 'overallRating')?.res ||
            defaultQuestionnaire.overallRatings) as LegacyOverallRatingsDefinition;
        const secondaryOverallRating = (sections.find(x => x.type === 'secondaryOverallRating')?.res ||
            defaultQuestionnaire.secondaryOverallRatings) as LegacySecondaryOverallRatingsDefinition;

        const legacySections = sections
            .filter(x => x.type === 'question' || x.type === 'heading')
            .map(x => x.res) as LegacySection[];

        return [
            {
                name: reviewQuestionnaire.name,
                description: reviewQuestionnaire.description,
                order: reviewQuestionnaire.order,
            },
            {
                ...defaultQuestionnaire,
                graph,
                overallRatings: overallRating,
                secondaryOverallRatings: secondaryOverallRating,
                sections: legacySections,
            },
        ];
    };
